import ContentBody from 'components/content-body/content-body';
import ContentHeaderTitle from 'components/content-header-title/content-header-title';
import ContentHeader from 'components/content-header/content-header';
import Content from 'components/content/content';
import { useCurrentBilikZones } from 'context/current-bilik-zones-context';
import { UniqueDataProvider } from 'context/unique-data-context/unique-data-context';
import { useDashboardPageProViewQuery } from 'generated/graphql';
import React, { useEffect, useMemo, useState } from 'react';
import { Loader } from 'semantic-ui-react';
import { NumberParam, useQueryParam } from 'use-query-params';
import DashboardContent from './dashboard-content';

const now = new Date();

const DashboardPage = (): JSX.Element => {
  const { currentBilikZones } = useCurrentBilikZones();

  const currentBilikZoneIds = useMemo(
    () => currentBilikZones?.map((zone) => zone.id),
    [currentBilikZones],
  );
  const [tradeId, setTradeId] = useQueryParam('tradeId', NumberParam);
  const [proViewIds, setProViewIds] = useState<number[] | undefined>(undefined);
  const [proViewPublishedIds, setProViewPublishedIds] = useState<
    number[] | undefined
  >(undefined);

  const [year, setYear] = useQueryParam('year', NumberParam);
  const [compareYear, setCompareYear] = useQueryParam(
    'compareYear',
    NumberParam,
  );

  // From 2011 to now
  const yearOptions = useMemo(
    () =>
      Array.from(Array(now.getFullYear() - 2011).keys()).map((index) => ({
        key: now.getFullYear() - index,
        value: now.getFullYear() - index,
        text: `${now.getFullYear() - index}`,
      })),
    [],
  );

  // Fetch all proViewIds for the current tradeId
  // We dont filter published pro or something else because this is used for flux data (mongodb call/sms)
  const { loading: proViewLoading, data: proViews } =
    useDashboardPageProViewQuery({
      variables: {
        where: {
          bilikZoneId: currentBilikZoneIds
            ? {
                _in: currentBilikZoneIds,
              }
            : undefined,
          proPresentation: tradeId
            ? {
                mainTrades: {
                  tradeId: {
                    _eq: tradeId,
                  },
                },
              }
            : undefined,
        },
      },
    });

  useEffect(() => {
    if (proViews?.proView.length) {
      setProViewIds(proViews.proView.map((proView) => proView.id));
      setProViewPublishedIds(
        proViews.proView
          .filter((proView) => proView.status === 'published')
          .map((proView) => proView.id),
      );
      return;
    }
    setProViewIds(undefined);
    setProViewPublishedIds(undefined);
  }, [proViews]);

  if (currentBilikZones === undefined || proViewLoading) {
    return (
      <Content>
        <Loader
          style={{ marginTop: '50px' }}
          size="large"
          active
          inline="centered"
        >
          Chargement...
        </Loader>
      </Content>
    );
  }

  return (
    <Content>
      <ContentHeader>
        <ContentHeaderTitle>
          Tableau de bord -{' '}
          {currentBilikZones
            ? currentBilikZones.map((zone) => zone.name).join(', ')
            : 'Toutes les zones'}
          <small style={{ fontWeight: 400 }}>
            Hors tests des responsables réseau (adresses @bilik.fr et numéros
            des utilisateurs de l&apos;admin)
          </small>
        </ContentHeaderTitle>
      </ContentHeader>
      <ContentBody>
        <UniqueDataProvider>
          <DashboardContent
            currentBilikZoneIds={currentBilikZoneIds}
            proViewIds={proViewIds}
            proViewPublishedIds={proViewPublishedIds}
            tradeId={tradeId}
            year={year}
            setYear={setYear}
            setTradeId={setTradeId}
            compareYear={compareYear}
            setCompareYear={setCompareYear}
            yearOptions={yearOptions}
          />
        </UniqueDataProvider>
      </ContentBody>
    </Content>
  );
};

export default DashboardPage;
